import {
  ComponentType,
  FunctionComponent,
  createContext,
  useState,
  useContext,
} from 'react';

import { CspProfile, Role, User } from '../types';
import {
  restoreProfile,
  restoreUser,
  saveProfile,
  saveUser,
} from '../utils/storage';

interface AuthContextProps {
  user?: User;
  roles?: Role[];
  profile?: CspProfile;
  setUser: (user: User) => void;
  setProfile: (profile: CspProfile) => void;
}

const parseRoles = (user?: User) => {
  return user?.profile
    ? user?.profile.csp_app_role ?? [Role.Viewer]
    : undefined;
};

const AuthContext = createContext<AuthContextProps>({
  /* eslint-disable @typescript-eslint/no-empty-function */
  setUser: () => {},
  setProfile: () => {},
  /* eslint-enable @typescript-eslint/no-empty-function */
});

export const AuthContextProvider: FunctionComponent = ({ children }) => {
  const [user, setUser] = useState<User | undefined>(restoreUser());
  const [roles, setRoles] = useState<Role[] | undefined>(
    parseRoles(restoreUser())
  );
  const [profile, setProfile] = useState<CspProfile | undefined>(
    restoreProfile()
  );

  const handleUserChange = (user: User) => {
    const userRoles = parseRoles(user);
    setUser(user);
    setRoles(userRoles);
    saveUser(user);
  };

  const handleProfileChange = (profile: CspProfile) => {
    setProfile(profile);
    saveProfile(profile);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        roles,
        profile,
        setUser: handleUserChange,
        setProfile: handleProfileChange,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);

export const withAuthContext =
  <P extends Record<string, never>>(WrappedComponent: ComponentType<P>) =>
  (props: P) =>
    (
      <AuthContext.Consumer>
        {(contexts) => <WrappedComponent {...props} {...contexts} />}
      </AuthContext.Consumer>
    );
